import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'

import { Add, ArrowBack } from '@mui/icons-material'
import { Alert, Box, Button, Card, CardContent, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material'

import ConfirmDialog from '../../../components/ConfirmDialog/ConfirmDialog'
import usePage from '../../../hooks/usePage'
import pageService from '../../../services/PageService'
import {
  useGetInternalCategoryFeaturesQuery,
  useGetInternalCategoryListQuery,
  useLazyGetInternalFeatureExampleQuery,
  useSaveInternalFeatureExampleMutation,
  useSaveInternalFeatureMutation,
} from '../../api/categories'
import { useGetInternalMarketsQuery } from '../../api/internal-markets'
import { useGetLanguageMapsQuery } from '../../api/language'
import { useGetInternalStoresQuery } from '../../api/stores'
import { DiscardChangesDialog } from '../../components/DiscardChangesDialog/DiscardChangesDialog'
import { InputTranslator } from '../../components/InputTranslator/InputTranslator'
import { QueryStateBoundary } from '../../components/QueryStateBoundary/QueryStateBoundary'
import { ChoiceEditDialog } from '../../features/categories/components/ChoiceEditDialog/ChoiceEditDialog'
import { ExampleEditDialog } from '../../features/categories/components/ExampleEditDialog/ExampleEditDialog'
import { FeatureChoicesTable } from '../../features/categories/components/FeatureChoicesTable/FeatureChoicesTable'
import { FeaturePositions } from '../../features/categories/components/FeaturePositions/FeaturePositions'
import {
  canSaveFeature,
  describePosition,
  ExampleDraft,
  exampleFieldLabelKeys,
  featureEditPath,
  featureFieldLabelKeys,
  FeatureChoiceValues,
  FeatureFormValues,
  FeaturePositionOption,
  findDraftForExample,
  findDraftForPosition,
  getAvailablePositions,
  getUnstoredExampleFields,
  getUnstoredFeatureFields,
  newExample,
  positionKey,
  rebaseFeatureFormValues,
  toFeatureFormValues,
  toFeatureSaveBody,
  upsertExampleDraft,
} from '../../features/categories/components/helpers/featureHelpers'
import { categoryLanguages } from '../../features/categories/helpers/categoryLanguageHelpers'
import { FeatureExample, FeatureSaveBody } from '../../features/categories/types/Categories'
import { getValidationErrorMessage } from '../../helpers/apiValidationError'
import { useEditPageLeave } from '../../hooks/useEditPageLeave'
import { useMutationFeedback } from '../../hooks/useMutationFeedback'

const listPath = '/internal/categories'

// The backdrop passes its click event as the confirmation, so a confirmation is recognised by its data.
const removeAction = 'remove'

type Removal = { kind: 'position'; position: FeaturePositionOption } | { kind: 'choice'; index: number }
type ChoiceEdit = { index?: number; choice?: FeatureChoiceValues }
type ExampleEdit = { position: FeaturePositionOption; marketIso: string; example: FeatureExample }

const flags = [
  { name: 'active', labelKey: 'internal-analysts:categories_feature_active' },
  { name: 'hidden', labelKey: 'internal-analysts:categories_feature_hidden' },
  { name: 'hideScreenshots', labelKey: 'internal-analysts:categories_feature_hide_screenshots' },
] as const

// No <form> here: the InputTranslator dialog has its own form, and React bubbles its submit through the portal into any parent form.
export const FeatureEditPage: FC = () => {
  const { categoryId = '', featureId } = useParams<{ categoryId: string; featureId?: string }>()
  usePage(pageService.getInternalPageWithId('internal-category-feature-edit'))
  const { t } = useTranslation()
  const { notify } = useMutationFeedback()
  const navigate = useNavigate()
  const categoriesQuery = useGetInternalCategoryListQuery()
  const featuresQuery = useGetInternalCategoryFeaturesQuery({ categoryId }, { skip: !categoryId })
  const languageMapsQuery = useGetLanguageMapsQuery()
  const storesQuery = useGetInternalStoresQuery()
  const marketsQuery = useGetInternalMarketsQuery()
  const [saveFeature] = useSaveInternalFeatureMutation()
  const [saveExample] = useSaveInternalFeatureExampleMutation()
  const [fetchExample] = useLazyGetInternalFeatureExampleQuery()

  const [drafts, setDrafts] = useState<ExampleDraft[]>([])
  const [exampleEdit, setExampleEdit] = useState<ExampleEdit>()
  const [loadingExampleKey, setLoadingExampleKey] = useState<string>()
  const [choiceEdit, setChoiceEdit] = useState<ChoiceEdit>()
  const [removal, setRemoval] = useState<Removal>()
  const [isSaving, setIsSaving] = useState(false)

  const languageMaps = languageMapsQuery.data
  const category = categoriesQuery.data?.find((candidate) => candidate.id === categoryId)
  const feature = featureId ? featuresQuery.data?.find((candidate) => candidate.id === featureId) : undefined

  const {
    control,
    reset,
    getValues,
    setValue,
    formState: { isDirty },
  } = useForm<FeatureFormValues>({ defaultValues: toFeatureFormValues(undefined, undefined) })
  const names = useWatch({ control, name: 'names' })
  const positions = useWatch({ control, name: 'positions' })
  const choices = useWatch({ control, name: 'choices' })
  const { guard, leave } = useEditPageLeave(isDirty || drafts.length > 0)
  const loadedKey = useRef<string>()
  const loadedValues = useRef<FeatureFormValues>()
  const loadKey = featureId ?? 'new'
  const isReady = !!languageMaps && (!featureId || !!feature)

  // Only on the first load of a feature, so a refetch after saving can't replace edits that weren't stored.
  useEffect(() => {
    if (isReady && loadedKey.current !== loadKey) {
      loadedKey.current = loadKey
      loadedValues.current = toFeatureFormValues(feature, languageMaps)
      reset(toFeatureFormValues(feature, languageMaps))
    }
  }, [isReady, loadKey, feature, languageMaps, reset])

  const positionOptions = useMemo(
    () => (positions ?? []).map((position) => describePosition(position, storesQuery.data, marketsQuery.data)),
    [positions, storesQuery.data, marketsQuery.data]
  )
  const availablePositions = useMemo(
    () => getAvailablePositions(storesQuery.data, marketsQuery.data, positions ?? []),
    [storesQuery.data, marketsQuery.data, positions]
  )
  const editedKeys = useMemo(() => drafts.map((draft) => positionKey(draft.position)), [drafts])

  const setPositions = (next: FeatureFormValues['positions']) => setValue('positions', next, { shouldDirty: true })
  const setChoices = useCallback((next: FeatureChoiceValues[]) => setValue('choices', next, { shouldDirty: true }), [setValue])

  const handleAddPosition = (option: FeaturePositionOption) => setPositions([...getValues('positions'), { storeId: option.storeId, marketId: option.marketId }])

  const handleRemoveConfirmed = () => {
    if (removal?.kind === 'position') {
      const key = positionKey(removal.position)
      setPositions(getValues('positions').filter((position) => positionKey(position) !== key))
      setDrafts((current) => current.filter((draft) => positionKey(draft.position) !== key))
    }
    if (removal?.kind === 'choice') setChoices(getValues('choices').filter((_, index) => index !== removal.index))
  }

  const handleEditExample = useCallback(
    async (option: FeaturePositionOption) => {
      const draft = findDraftForPosition(drafts, option)
      if (draft) {
        setExampleEdit({ position: option, marketIso: draft.marketIso, example: draft.example })
        return
      }
      if (!option.marketIso) {
        notify(t('internal-analysts:categories_example_unknown_market', { position: option.name }), 'error')
        return
      }
      const marketIso = option.marketIso
      if (!featureId) {
        setExampleEdit({ position: option, marketIso, example: newExample(option.storeId, marketIso) })
        return
      }
      setLoadingExampleKey(positionKey(option))
      try {
        const fetched = await fetchExample({ featureId, position: { storeId: option.storeId, marketIso } }, false).unwrap()
        const example = findDraftForExample(drafts, fetched)?.example ?? fetched ?? newExample(option.storeId, marketIso)
        setExampleEdit({ position: option, marketIso, example })
      } catch {
        notify(t('internal-analysts:categories_example_load_failed', { position: option.name }), 'error')
      } finally {
        setLoadingExampleKey(undefined)
      }
    },
    [drafts, featureId, fetchExample, notify, t]
  )

  const handleExampleDone = (example: FeatureExample) => {
    if (!exampleEdit) return
    const { position, marketIso } = exampleEdit
    setDrafts((current) =>
      upsertExampleDraft(current, { position: { storeId: position.storeId, marketId: position.marketId }, marketIso, positionName: position.name, example })
    )
    setExampleEdit(undefined)
    notify(t('internal-analysts:categories_example_kept'), 'info')
  }

  const handleChoiceSave = (values: FeatureChoiceValues) => {
    const current = getValues('choices')
    if (choiceEdit?.index === undefined) setChoices([...current, values])
    else setChoices(current.map((choice, index) => (index === choiceEdit.index ? { ...choice, ...values, key: choice.key, source: choice.source } : choice)))
    setChoiceEdit(undefined)
  }

  // The feature and language maps as stored now, so an update carries only this page's edits over what others saved since it opened.
  const readLatestFeature = async () => {
    try {
      const [features, maps] = await Promise.all([featuresQuery.refetch().unwrap(), languageMapsQuery.refetch().unwrap()])
      const latest = features.find((candidate) => candidate.id === featureId)
      if (!latest) notify(t('internal-analysts:categories_feature_not_found'), 'error')
      return latest && { feature: latest, languageMaps: maps }
    } catch {
      notify(t('internal-analysts:categories_feature_latest_load_failed'), 'error')
      return undefined
    }
  }

  const toSaveBody = async (values: FeatureFormValues): Promise<FeatureSaveBody | undefined> => {
    if (!featureId) return toFeatureSaveBody(undefined, categoryId, languageMaps, values)
    const loaded = loadedKey.current === featureId && loadedValues.current ? loadedValues.current : toFeatureFormValues(feature, languageMaps)
    const latest = await readLatestFeature()
    if (!latest) return undefined
    const current = toFeatureFormValues(latest.feature, latest.languageMaps)
    return toFeatureSaveBody(latest.feature, categoryId, latest.languageMaps, rebaseFeatureFormValues(loaded, values, current))
  }

  const handleSave = async () => {
    if (!languageMaps) return
    const values = getValues()
    setIsSaving(true)
    try {
      const body = await toSaveBody(values)
      if (!body) return
      const name = body.names.en ?? body.name
      let result
      try {
        result = await saveFeature(body).unwrap()
      } catch (error) {
        const reason = getValidationErrorMessage(error)
        notify(
          reason ? t('internal-analysts:categories_feature_save_failed_reason', { reason }) : t('internal-analysts:categories_feature_save_failed'),
          'error'
        )
        return
      }
      const problems: string[] = []
      const unstored = getUnstoredFeatureFields(body, result)
      if (unstored.length) {
        problems.push(t('internal-analysts:categories_feature_not_stored', { fields: unstored.map((field) => t(featureFieldLabelKeys[field])).join(', ') }))
      }
      const savedId = result.id
      if (!savedId) {
        if (drafts.length) problems.push(t('internal-analysts:categories_examples_not_saved_no_id'))
      } else {
        const failed: ExampleDraft[] = []
        for (const draft of drafts) {
          const example = { ...draft.example, featureId: savedId }
          try {
            const { stored } = await saveExample({ example, position: { storeId: draft.position.storeId, marketIso: draft.marketIso } }).unwrap()
            const fields = getUnstoredExampleFields(example, stored)
            if (fields.length) {
              problems.push(
                t('internal-analysts:categories_example_not_stored', {
                  position: draft.positionName,
                  fields: fields.map((field) => t(exampleFieldLabelKeys[field])).join(', '),
                })
              )
            }
          } catch {
            failed.push(draft)
            problems.push(t('internal-analysts:categories_example_save_failed', { position: draft.positionName }))
          }
        }
        setDrafts(failed)
      }
      if (problems.length) {
        notify(problems.join(' '), 'error')
        // A retry has to update the created feature rather than create a second one.
        if (!featureId && savedId) leave(featureEditPath(categoryId, savedId), { replace: true })
        return
      }
      notify(
        featureId
          ? t('internal-analysts:categories_feature_saved', { name })
          : t('internal-analysts:categories_feature_added', { name, category: category?.name ?? categoryId }),
        'success'
      )
      leave(listPath)
    } finally {
      setIsSaving(false)
    }
  }

  const isLoading = featuresQuery.isLoading || languageMapsQuery.isLoading
  const loadError = featuresQuery.isError
    ? t('internal-analysts:categories_features_load_failed')
    : languageMapsQuery.isError
    ? t('internal-analysts:categories_feature_language_load_failed')
    : featureId && featuresQuery.data && !feature
    ? t('internal-analysts:categories_feature_not_found')
    : undefined
  const canSave = isReady && !isSaving && canSaveFeature({ names, positions, choices }) && (isDirty || drafts.length > 0)

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2} spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button startIcon={<ArrowBack />} onClick={() => navigate(listPath)}>
            {t('internal-common:back')}
          </Button>
          <Box>
            <Typography variant="h5" fontWeight="bold">
              {featureId ? t('internal-analysts:categories_feature_edit') : t('internal-analysts:categories_feature_new')}
            </Typography>
            {category && (
              <Typography variant="body2" color="text.secondary">
                {t('internal-analysts:categories_feature_in_category', { category: category.name })}
              </Typography>
            )}
          </Box>
        </Stack>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          {t('internal-common:save')}
        </Button>
      </Stack>

      <QueryStateBoundary isLoading={isLoading} isError={!!loadError} errorText={loadError} skeletonHeight={480} skeletonSx={{ maxWidth: 960 }}>
        <Stack spacing={2} maxWidth={960}>
          <Alert severity="warning">{t('internal-analysts:categories_feature_customer_note')}</Alert>
          <Card>
            <CardContent>
              <Stack spacing={3}>
                <Box>
                  <Controller
                    name="names"
                    control={control}
                    render={({ field }) => (
                      <InputTranslator
                        value={field.value}
                        onChange={field.onChange}
                        label={t('internal-analysts:categories_feature_name')}
                        dialogTitle={t('internal-analysts:categories_feature_name')}
                        selectedLanguages={categoryLanguages}
                        disabled={isSaving}
                      />
                    )}
                  />
                  {!names?.en?.trim() && (
                    <Typography variant="caption" color="error.main">
                      {t('internal-analysts:categories_feature_name_required')}
                    </Typography>
                  )}
                </Box>
                <Controller
                  name="alternateNames"
                  control={control}
                  render={({ field }) => (
                    <InputTranslator
                      value={field.value}
                      onChange={field.onChange}
                      label={t('internal-analysts:categories_feature_alternate_name')}
                      dialogTitle={t('internal-analysts:categories_feature_alternate_name')}
                      selectedLanguages={categoryLanguages}
                      disabled={isSaving}
                    />
                  )}
                />
                <Stack direction="row" spacing={2} flexWrap="wrap">
                  {flags.map((flag) => (
                    <Controller
                      key={flag.name}
                      name={flag.name}
                      control={control}
                      render={({ field }) => (
                        <FormControlLabel
                          control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={isSaving} />}
                          label={t(flag.labelKey)}
                        />
                      )}
                    />
                  ))}
                </Stack>
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Typography variant="h6">{t('internal-analysts:categories_feature_positions')}</Typography>
                <Alert severity="info">{t('internal-analysts:categories_example_shared_note')}</Alert>
                <FeaturePositions
                  positions={positionOptions}
                  available={availablePositions}
                  disabled={isSaving}
                  editedKeys={editedKeys}
                  loadingKey={loadingExampleKey}
                  onAdd={handleAddPosition}
                  onRemove={(position) => setRemoval({ kind: 'position', position })}
                  onEditExample={handleEditExample}
                />
                {!positions?.length && (
                  <Typography variant="caption" color="error.main">
                    {t('internal-analysts:categories_feature_positions_required')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>

          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="h6">{t('internal-analysts:categories_feature_choices')}</Typography>
                  <Button variant="outlined" startIcon={<Add />} onClick={() => setChoiceEdit({})} disabled={isSaving}>
                    {t('internal-analysts:categories_choice_add')}
                  </Button>
                </Stack>
                <FeatureChoicesTable
                  choices={choices ?? []}
                  disabled={isSaving}
                  onEdit={(index) => setChoiceEdit({ index, choice: getValues('choices')[index] })}
                  onRemove={(index) => setRemoval({ kind: 'choice', index })}
                  onReorder={setChoices}
                />
                {!choices?.length && (
                  <Typography variant="caption" color="error.main">
                    {t('internal-analysts:categories_feature_choices_required')}
                  </Typography>
                )}
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </QueryStateBoundary>

      <ChoiceEditDialog open={!!choiceEdit} choice={choiceEdit?.choice} onClose={() => setChoiceEdit(undefined)} onSave={handleChoiceSave} />
      <ExampleEditDialog
        open={!!exampleEdit}
        positionName={exampleEdit?.position.name ?? ''}
        example={exampleEdit?.example}
        onClose={() => setExampleEdit(undefined)}
        onDone={handleExampleDone}
      />
      <ConfirmDialog
        open={!!removal}
        confirmDialogData={{
          title: removal?.kind === 'choice' ? t('internal-analysts:categories_choice_remove') : t('internal-analysts:categories_position_remove'),
          confirmText:
            removal?.kind === 'choice'
              ? t('internal-analysts:categories_choice_remove_confirm')
              : t('internal-analysts:categories_position_remove_confirm', { position: removal?.kind === 'position' ? removal.position.name : '' }),
          actionText: t('internal-analysts:categories_remove'),
          destructiveAction: true,
          data: removeAction,
        }}
        onClose={(confirmed) => {
          if (confirmed?.data === removeAction) handleRemoveConfirmed()
          setRemoval(undefined)
        }}
      />
      <DiscardChangesDialog
        guard={guard}
        title={t('internal-analysts:categories_feature_discard_title')}
        text={t('internal-analysts:categories_feature_discard_confirm')}
        discardText={t('internal-analysts:categories_feature_discard')}
      />
    </Box>
  )
}
